import {
  Component,
  ElementRef,
  output,
  input,
  signal,
  computed,
  inject,
  model,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzIconModule } from 'ng-zorro-antd/icon';
import {
  DocumentationAttachment,
  DocumentationService,
  I18nService,
  UiService,
  Upload,
} from '@lp-ui-lib';

@Component({
  selector: 'app-docu-item',
  imports: [CommonModule, NzModalModule, NzIconModule],
  templateUrl: './documentation-item.html',
  styleUrl: './documentation-item.scss',
})
export class DocumentationItemComponent {
  // ---------- Inputs (Signals) ----------
  header = input<string | null>(null);
  svg_icon = input<string | null>(null);
  variant = input<string>('text');

  content = model<DocumentationAttachment[]>([]);

  documentationId = input<number>(-1);

  // ---------- Outputs (Signals) ----------
  clickEvent = output<void>();
  documentationError = output<boolean>();

  // ---------- View Refs ----------
  @ViewChild('imageInputUpload') imageInputUpload!: ElementRef<HTMLInputElement>;
  @ViewChild('imageInputCamera') imageInputCamera!: ElementRef<HTMLInputElement>;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  // ---------- Injected Services ----------
  private service = inject(DocumentationService);
  public i18n = inject(I18nService);
  public ui = inject(UiService);

  // ---------- Local UI State (Signals) ----------
  fileSizeError = signal(false);
  takeOrUploadDialogOpen = signal(false);

  imageUploadProgress = signal<Upload | undefined>(undefined);
  fileUploadProgress = signal<Upload | undefined>(undefined);
  isMobile = computed<boolean>(() => this.ui.IsMobile());
  isTablet = computed<boolean>(() => this.ui.IsTablet());
  isDesktop = computed<boolean>(() => this.ui.IsDesktop());

  // ---------- Safe progress values (0..100) to avoid non-finite error ----------
  imageProgressValue = computed<number>(() => this.clampProgress(this.imageUploadProgress()));
  fileProgressValue = computed<number>(() => this.clampProgress(this.fileUploadProgress()));

  // ---------- UI Actions ----------
  onHeaderClick(): void {
    const variant = this.variant();
    if (variant === 'image') {
      if (!this.isDesktop()) {
        this.openAddImageModal();
      } else {
        this.uploadImage();
      }
    } else if (variant === 'file') {
      this.addFile();
    }
    this.clickEvent.emit();
  }

  onAllButtonClick(): void {
    // If it's image or file, the header click handles it already.
    const variant = this.variant();
    if (variant === 'image' || variant === 'file') return;
    this.clickEvent.emit();
  }

  // ---------- File Handling ----------
  fileChanged(event: Event, docType: 'image' | 'file'): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    const LIMIT = 20 * 1024 * 1024; // 20MB
    const DELAY = 1000;

    // Reset errors
    this.fileSizeError.set(false);
    this.documentationError.emit(false);

    // Deferred check for size error to match UX (as in your original)
    setTimeout(() => {
      const tooLarge = files.some((f) => f.size >= LIMIT);
      this.fileSizeError.set(tooLarge);
    }, DELAY);

    // Upload acceptable files

    files
      .filter((file) => file.size < LIMIT)
      .forEach((file) => {
        const reader = new FileReader();
        reader.onload = () => {
          const fileData = String(reader.result ?? '');
          const base64 = fileData.includes(',') ? fileData.split(',')[1] : fileData;

          // show fake initial progress
          const start: Upload = { state: 'IN_PROGRESS', progress: 0, event: null };
          if (docType === 'image') this.imageUploadProgress.set(start);
          else this.fileUploadProgress.set(start);

          this.service
            .uploadEventAttachment(this.documentationId(), docType, file.name, base64)
            .then((attachment: any) => {
              this.content.update((list) => [...list, attachment]);

              // clear progress
              if (docType === 'image') this.imageUploadProgress.set(undefined);
              else this.fileUploadProgress.set(undefined);
            })
            .catch((e: any) => {
              if (e?.status === 403) this.documentationError.emit(true);
              console.error('uploadEventAttachment failed', e);
              if (docType === 'image') this.imageUploadProgress.set(undefined);
              else this.fileUploadProgress.set(undefined);
            })
            .finally(() => {
              // allow uploading same file again
              input.value = '';
            });
        };

        reader.readAsDataURL(file);
      });

    // Fake a transient progress for over-limit files (to give user feedback)
    files
      .filter((file) => file.size >= LIMIT)
      .forEach(() => {
        const inProgress: Upload = {
          progress: 0,
          state: 'IN_PROGRESS',
          event, // carry the original event for parity with your previous code
        };
        if (docType === 'image') {
          this.imageUploadProgress.set(inProgress);
        } else {
          this.fileUploadProgress.set(inProgress);
        }

        setTimeout(() => {
          this.imageUploadProgress.set(undefined);
          this.fileUploadProgress.set(undefined);
        }, DELAY);
      });
  }

  // ---------- Image dialog & triggers ----------
  openAddImageModal(): void {
    this.takeOrUploadDialogOpen.set(true);
  }

  uploadImage(): void {
    this.takeOrUploadDialogOpen.set(false);
    this.imageInputUpload?.nativeElement?.click();
  }

  captureImage(): void {
    this.takeOrUploadDialogOpen.set(false);
    this.imageInputCamera?.nativeElement?.click();
  }

  addFile(): void {
    this.fileInput?.nativeElement?.click();
  }

  // ---------- Delete ----------
  deleteAttachment(attachment: DocumentationAttachment): void {
    this.documentationError.emit(false);

    this.service
      .deleteEventAttachment(attachment.attachmentId)
      .then(() => {
        this.content.update((list) =>
          list.filter((item) => item.attachmentId !== attachment.attachmentId)
        );
      })
      .catch((e: any) => {
        if (e?.status === 403) {
          this.documentationError.emit(true);
        } else {
          // Optional: log or surface non-403 errors
          console.error('deleteEventAttachment failed', e);
        }
      });
  }

  // ---------- Helpers ----------
  private clampProgress(u?: Upload): number {
    const raw = Number(u?.progress ?? 0);
    if (!Number.isFinite(raw)) return 0;
    return Math.max(0, Math.min(100, raw));
  }
}
